import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Input,
  OnChanges,
  signal,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbilityServiceSignal } from '@casl/angular';
import { TranslatePipe } from '@ngx-translate/core';
import { captureException } from '@sentry/browser';
import { ProjectsGateway } from '@wilson/api/gateway';
import { AuthorizationModule } from '@wilson/authorization';
import { AgreementsCacheService } from '@wilson/clients/services';
import { FeatureFlagsModule } from '@wilson/feature-flags';
import {
  ActivityDeviationStatus,
  Agreement,
  GeoLocation,
  Project,
  RoleAction,
  RolePermissionSubject,
  Shift,
  ShiftCategoriesColorCode,
} from '@wilson/interfaces';
import { LabelsModule } from '@wilson/labels';
import { PriorityModule } from '@wilson/non-domain-specific/priority';
import { AgreementsToTextPipe } from '@wilson/pipes';
import { ProjectStatusTagComponent } from '@wilson/projects/components';
import { ShiftCoreModule } from '@wilson/shift/core';
import { ShiftsService } from '@wilson/shifts';
import { DateTimeFormat } from '@wilson/utils';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { catchError, map, Observable, of, shareReplay, switchMap } from 'rxjs';
import { TooltipShiftScheduleDetailsComponent } from '../tooltip-shift-schedule-details/tooltip-shift-schedule-details.component';
import { AgreementClientCostCenterNameRendererComponent } from '@wilson/non-domain-specific/agreement-helper';
import { CreateNewViaDragAndDropService } from '@wilson/shift-timeline/services';

@Component({
  selector: 'wilson-timeline-shift-tooltip',
  templateUrl: './timeline-shift-tooltip.component.html',
  styleUrls: ['./timeline-shift-tooltip.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    NzTagModule,
    NzDividerModule,
    TranslatePipe,
    ShiftCoreModule,
    PriorityModule,
    FeatureFlagsModule,
    LabelsModule,
    ProjectStatusTagComponent,
    AuthorizationModule,
    TooltipShiftScheduleDetailsComponent,
    NzTooltipDirective,
    AgreementsToTextPipe,
    AgreementClientCostCenterNameRendererComponent,
  ],
})
export class TimeLineShiftTooltipComponent implements OnChanges {
  private readonly shiftsService = inject(ShiftsService);
  private readonly projectsGateway = inject(ProjectsGateway);
  private readonly agreementsCacheService = inject(AgreementsCacheService);
  private readonly destroyRef = inject(DestroyRef);

  @Input() shift: Shift | null = null;
  @Input() plannedShiftStartDateTime = '';
  @Input() plannedShiftEndDateTime = '';
  @Input() accurateShiftStartDateTime = '';
  @Input() shiftStartTimeDifference!: number;
  @Input() accurateShiftEndDateTime = '';
  @Input() shiftEndTimeDifference!: number;
  @Input() shiftStartTimeType!: ActivityDeviationStatus;
  @Input() shiftEndTimeType!: ActivityDeviationStatus;
  @Input() plannedStartLocation:
    | Pick<GeoLocation, 'name' | 'locationCode'>
    | undefined
    | null = null;
  @Input() plannedEndLocation:
    | Pick<GeoLocation, 'name' | 'locationCode'>
    | undefined
    | null = null;
  @Input() reportedStartLocation:
    | Pick<GeoLocation, 'name' | 'locationCode'>
    | null
    | undefined = null;
  @Input() reportedEndLocation:
    | Pick<GeoLocation, 'name' | 'locationCode'>
    | null
    | undefined = null;
  @Input() renderingLoadingOrValidations = false;

  protected DateTimeFormat = DateTimeFormat;
  protected shiftCategoriesColorCode = ShiftCategoriesColorCode;
  protected ActivityDeviationStatus = ActivityDeviationStatus;

  protected viaLocations = '';
  protected project$!: Observable<Project>;
  protected uniqueProfessionIds$!: Observable<string[]>;
  protected readonly shiftCommentSignal = signal<string | null | undefined>(
    null,
  );
  protected readonly shiftPlanningCommentSignal = signal<
    string | null | undefined
  >(null);
  protected readonly agreementsLengthSignal = signal(0);

  protected RoleAction = RoleAction;
  protected RolePermissionSubject = RolePermissionSubject;
  protected can = inject(AbilityServiceSignal).can;
  protected agreements$: Observable<Agreement[] | null> = of(null);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['shift'] && this.shift) {
      const startLocations = new Set(
        this.shift.activities?.map((item) => item.startLocation?.name),
      );

      const endLocations = new Set(
        this.shift.activities?.map((item) => item.endLocation?.name),
      );

      const combinedSet = new Set([...startLocations, ...endLocations]);

      combinedSet.delete(undefined);

      this.viaLocations = [...combinedSet.values()].join(', ');

      if (this.shift.projectId) {
        this.project$ = this.projectsGateway.getProjectById(
          this.shift.projectId,
        );
      }

      // trigger http calls only if state has loaded proper shift id
      if (this.shift.id !== CreateNewViaDragAndDropService.MOCK_SHIFT_ID) {
        const shift$ = this.shiftsService
          .get(this.shift.id, {
            relations: ['activities'],
          })
          .pipe(
            shareReplay(),
            catchError((error) => {
              captureException(error);
              return of(null);
            }),
          );

        this.uniqueProfessionIds$ = shift$.pipe(
          map((shift) =>
            Array.from(
              new Set(
                (shift?.activities || [])
                  .map((activity) => activity.professionId)
                  .filter(
                    (professionId): professionId is string =>
                      professionId !== null,
                  ),
              ),
            ),
          ),
        );

        this.agreements$ = shift$.pipe(
          switchMap((shift) => {
            const agreementIds =
              shift?.activities
                ?.map((activity) => activity.agreementId)
                .filter((id): id is string => !!id) ?? [];

            const uniqueAgreementIdsSet = new Set(agreementIds);
            const uniqueAgreementArray = Array.from(uniqueAgreementIdsSet);

            this.agreementsLengthSignal.set(uniqueAgreementArray.length);

            if (uniqueAgreementArray.length > 0) {
              return this.agreementsCacheService.getAgreementsFromCache(
                uniqueAgreementArray,
              );
            } else {
              return of(null);
            }
          }),
        );

        shift$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((shift) => {
          this.shiftCommentSignal.set(shift?.comment);
          this.shiftPlanningCommentSignal.set(shift?.planningComment);
        });
      }
    }
  }
}
